import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, ROLES } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import StaffDashboard from './pages/StaffDashboard';
import LiveStaffMap from './pages/LiveStaffMap';
import PayrollCentre from './pages/PayrollCentre';
import AwardRates from './pages/AwardRates';
import StaffPayrollProfiles from './pages/StaffPayrollProfiles';
import ManagerDashboard from './pages/ManagerDashboard';
import Visits from './pages/Visits';
import RosterBoard from './pages/RosterBoard';
import VisitDetail from './pages/VisitDetail';
import Timesheets from './pages/Timesheets';
import Mileage from './pages/Mileage';
import Participants from './pages/Participants';
import ParticipantDetail from './pages/ParticipantDetail';
import Clients from './pages/Clients';
import StaffManagement from './pages/StaffManagement';
import Settings from './pages/Settings';
import ShiftReports from './pages/ShiftReports';

import './App.css';
import AuditTrail from './pages/AuditTrail';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Dashboard router based on role
const DashboardRouter = () => {
  const { user, isStaff } = useAuth();
  
  if (isStaff) {
    return <StaffDashboard />;
  }
  return <ManagerDashboard />;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route 
        path="/register" 
        element={user ? <Navigate to="/" replace /> : <Register />} 
      />

      {/* Protected routes - All roles */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roster"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <RosterBoard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits"
        element={
          <ProtectedRoute>
            <Visits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/:visitId"
        element={
          <ProtectedRoute>
            <VisitDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timesheets"
        element={
          <ProtectedRoute>
            <Timesheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mileage"
        element={
          <ProtectedRoute>
            <Mileage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Admin/Super Admin only routes */}
      <Route
        path="/participants"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <Participants />
          </ProtectedRoute>
        }
      />
      <Route
        path="/participants/:participantId"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <ParticipantDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <StaffManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/shift-reports"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <ShiftReports />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />

      <Route
        path="/live-staff-map"
        element={
          <ProtectedRoute>
            <LiveStaffMap />
          </ProtectedRoute>
        }
      />

      <Route
        path="/payroll-centre"
        element={
          <ProtectedRoute>
            <PayrollCentre />
          </ProtectedRoute>
        }
      />

      <Route
        path="/award-rates"
        element={
          <ProtectedRoute
            allowedRoles={[
              ROLES.SUPER_ADMIN,
              ROLES.ADMIN,
            ]}
          >
            <AwardRates />
          </ProtectedRoute>
        }
      />

      <Route
        path="/staff-payroll"
        element={
          <ProtectedRoute
            allowedRoles={[
              ROLES.SUPER_ADMIN,
              ROLES.ADMIN,
            ]}
          >
            <StaffPayrollProfiles />
          </ProtectedRoute>
        }
      />
              <Route path="/audit-trail" element={<AuditTrail />} />
        </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
